import { useCallback, useEffect } from "react";
import { ResourceTypeLink } from "@evergis/api";
import { useDispatch, useSelector } from "react-redux";

import { goToCatalog } from "ducks/location/location";
import { resetSelected } from "ducks/catalog/catalog";
import { resetDialog } from "ducks/dialogs/dialogs";
import { reset } from "ducks/project/project";
import { getProjectName } from "ducks/project/selectors";
import { useNotification } from "hooks/notifications/useNotification";
import { deleteResources } from "resources/resources";
import { getTablesRefs, isLayerRef } from "../../../../resources/tables";
import { useDeleteResourcesNotifications } from "./useDeleteResourcesNotifications";
import { getCurrentTab, isLocationMainSelector } from "../../../../ducks/location/selectors";
import { useDeleteLayer } from "../../../../hooks/layer/useDeleteLayer";
import { CheckedDeleteResourcesState } from "./useDeleteResourcesChoice";

type ResourceNames = {
  name: string;
  alias: string;
};

export type ResourcesNamesInfo = {
  projects: ResourceNames[];
  layers: ResourceNames[];
  tables: ResourceNames[];
};

type DeleteResourcesResult = [VoidFunction, boolean];

const checkResourceDeleted = (data): number => data && data.results.length;
const checkProjectDeleted = (projects, data): number => projects.length === 1 && checkResourceDeleted(data);

// eslint-disable-next-line max-lines-per-function
export const useDeleteResources = (
  { projects, layers, tables }: ResourcesNamesInfo,
  resourceType: ResourceTypeLink,
  ableToDelete?: CheckedDeleteResourcesState,
  customClose?: boolean,
): DeleteResourcesResult => {
  const dispatch = useDispatch();
  const isLocationProject = useSelector(isLocationMainSelector);
  const currentProject = useSelector(getProjectName);
  const locationTab = useSelector(getCurrentTab);
  const [resourcesMutation, { loading: onDeleteLoading, data }] = deleteResources.use();
  const results = data && data.results;
  const notificationInfo = useDeleteResourcesNotifications({ projects, layers, tables }, resourceType, results);
  const { deleteLayerFromProject, deleteLayerFromCatalog } = useDeleteLayer();

  useNotification(
    results,
    results && {
      success: true,
      ...notificationInfo,
    },
  );

  useEffect(() => {
    if (!customClose && checkProjectDeleted(projects, data) && isLocationProject) {
      dispatch(goToCatalog({ tab: ResourceTypeLink.Project }));
    }
  }, [data, projects, isLocationProject, customClose]);

  const deleteResourcesHandler = useCallback(async () => {
    const projectNames = getNames(projects);
    let layerNames = getNames(layers);
    const tableNames = getNames(tables);

    if (ableToDelete.layers && resourceType === ResourceTypeLink.Table) {
      layerNames = await getTablesRefs(tableNames, isLayerRef);
    }

    if (locationTab) {
      await resourcesMutation({ input: { projects: projectNames, tables: tableNames, layers: layerNames } });
    } else {
      if (tableNames.length) {
        await resourcesMutation({ input: { tables: tableNames } });
      }

      if (layerNames.length) {
        await Promise.all(
          layerNames.map((layerName: string) => {
            deleteLayerFromProject(layerName);

            if (ableToDelete.layers) {
              return deleteLayerFromCatalog(layerName);
            }
          }),
        );
      }
    }

    if (!customClose) {
      dispatch(resetDialog());
      dispatch(resetSelected());

      projectNames.length && projectNames.includes(currentProject) && dispatch(reset());
    }
  }, [
    projects,
    layers,
    tables,
    ableToDelete.layers,
    resourceType,
    locationTab,
    dispatch,
    currentProject,
    resourcesMutation,
    deleteLayerFromProject,
    deleteLayerFromCatalog,
    customClose,
  ]);

  return [deleteResourcesHandler, onDeleteLoading];
};

function getNames(resources: ResourceNames[]): string[] {
  return resources.map(({ name }) => name);
}
